// v51 — Company switcher.
//
// Which company is this person acting for? Full reasoning in
// Product-KT/08-Decision-Acting-Context.md.
//
// WHEN IT APPEARS
//
// Only when the user belongs to more than one company — today that is 1 user in
// 31. Everyone else keeps the static tenant pill that was here before, so the
// overwhelming majority of people see no change at all.
//
// WHAT IT IS NOT
//
// It is not authorisation. Choosing a company sets two request headers; the
// server validates them against memberships on every single request and refuses
// anything the caller is not entitled to. A switcher the server trusted would be
// the same defect this work exists to remove.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useActingContext, useSetActingCompany } from "@/lib/context/useActingContext";

const KIND_LABEL: Record<string, string> = {
  coordinator: "Buys labour",
  partner: "Supplies labour",
  hybrid: "Buys & supplies",
};

interface Props {
  /** The profile's own company name, shown until memberships hydrate. */
  fallbackName?: string | null;
}

export function CompanySwitcher({ fallbackName }: Props = {}) {
  const { company, memberships, canSwitchCompany, hydrated } = useActingContext();
  const setCompany = useSetActingCompany();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  /* Click outside closes it. */
  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onEsc = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  /* Until the memberships arrive, show the profile's own company name so the
     bar does not flicker empty on first paint. Not interactive — we do not yet
     know whether there is anything to switch between. */
  if (!hydrated || !company) {
    if (!fallbackName) return null;
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-lc-blue-light text-lc-blue-dark border border-lc-blue-border whitespace-nowrap"
        title={`Active tenant: ${fallbackName}`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-lc-blue" />
        {fallbackName}
      </span>
    );
  }

  /* ONE COMPANY — the pill exactly as it was. No new affordance, no new
     question for a user who has nothing to choose between. */
  if (!canSwitchCompany) {
    return (
      <span
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-lc-blue-light text-lc-blue-dark border border-lc-blue-border whitespace-nowrap"
        title={`Active tenant: ${company.name ?? ""}`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-lc-blue" />
        {company.name}
      </span>
    );
  }

  const choose = (companyId: string) => {
    if (companyId === company.company_id) { setOpen(false); return; }
    setCompany(companyId);
    setOpen(false);
    /* Chapter 8 Part 8 Q3 — return to the dashboard. Staying put can leave the
       user on a screen that does not exist for the company they just switched
       to, which reads as the app breaking rather than as a switch. */
    navigate("/dashboard");
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={`Acting as ${company.name}. Change company`}
        /* v51 — the pill sits on the dark topbar. A blue-tint-on-white pill
           reads as a bright block against navy, so it takes the shell's own
           surface and the accent blue that was designed for dark ground. */
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lc-tag text-[11px] font-semibold whitespace-nowrap transition-colors cursor-pointer"
        style={{
          background: "var(--lc-shell-2)",
          border: "1px solid var(--lc-shell-line)",
          color: "var(--lc-shell-accent)",
        }}
        title={`Acting as ${company.name} — click to switch`}
      >
        <span className="w-1.5 h-1.5 rounded-full bg-lc-blue" />
        {company.name}
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {open && (
        <div
          role="listbox"
          aria-label="Choose a company"
          className="absolute right-0 mt-2 w-64 bg-white rounded-lc-md border border-lc-line shadow-xl z-20 overflow-hidden"
        >
          <div className="px-3 py-2 border-b border-lc-line">
            <div className="text-[10px] font-extrabold uppercase tracking-widest text-lc-faint">
              Acting as
            </div>
            <div className="text-[11px] text-lc-mute mt-0.5 leading-snug">
              Everything on screen belongs to the company you pick.
            </div>
          </div>

          {memberships.map(m => {
            const active = m.company_id === company.company_id;
            return (
              <button
                key={m.company_id}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => choose(m.company_id)}
                className={`w-full text-left px-3 py-2.5 flex items-start gap-2 transition-colors ${
                  active ? "bg-lc-blue-light" : "hover:bg-lc-bg-2"
                }`}
              >
                <span
                  className={`w-1.5 h-1.5 rounded-full mt-1.5 ${active ? "bg-lc-blue" : "bg-lc-line"}`}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-[12px] font-bold text-lc-ink truncate">
                    {m.name}
                  </span>
                  <span className="block text-[10px] text-lc-mute mt-0.5">
                    {KIND_LABEL[m.kind] ?? m.kind}
                    {m.role ? ` · ${m.role}` : ""}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
